import { compareAsc, compareDesc } from "date-fns";
import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  toValue,
} from "vue";

import type { SortDirection } from "@/components/archive/NewsFeed/ActionBarOptions.vue";
import type { ContentBaseItem } from "@/types/content.d.ts";

/*
 * versionCreated should always be a valid date.
 * If we ever receive an invalid/missing value, sorting can become unstable.
 * Use a fixed fallback date so the sort order stays deterministic.
 */
function safeDate(value: string | undefined): Date {
  const date = new Date(value ?? "");
  return Number.isNaN(date.getTime()) ? new Date(0) : date;
}

function useSearchResultsItems(
  initialSearchResults: Ref<ContentBaseItem[]>,
  socketItems:
    ComputedRef<ContentBaseItem[]> | MaybeRefOrGetter<ContentBaseItem[]>,
  loadMoreItems: MaybeRefOrGetter<ContentBaseItem[]> = [],
  sortDirection: MaybeRefOrGetter<SortDirection> = "desc",
): ComputedRef<ContentBaseItem[]> {
  const searchResults = computed(() => {
    const seen = new Set();
    return [
      ...initialSearchResults.value,
      ...toValue(loadMoreItems),
      ...toValue(socketItems),
    ]
      .filter((item) => {
        const duplicate = seen.has(item.urn);
        seen.add(item.urn);
        return !duplicate;
      })
      .sort((a, b) => {
        const dateA = safeDate(a.displayDateTime ?? a.versionCreated);
        const dateB = safeDate(b.displayDateTime ?? b.versionCreated);
        if (toValue(sortDirection) === "desc") {
          return compareDesc(dateA, dateB);
        }
        return compareAsc(dateA, dateB);
      });
  });

  return searchResults;
}

export default useSearchResultsItems;
